import { useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import { apiClient, POLE_LABELS, formatDate } from "@/lib/api";
import { usePoles } from "@/context/PolesContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Envelope, FloppyDisk, Info, CheckCircle, XCircle, PaperPlaneTilt, UserPlus, Trash, Key, PencilSimple, User as UserIcon, Copy, Buildings, Plus, Power } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ROLES = [
  { id: "admin", label: "Administrateur", desc: "Accès total (tous pôles + admin)" },
  { id: "responsable", label: "Responsable", desc: "Gère un pôle (lecture + attribution)" },
  { id: "employe", label: "Employé", desc: "Traite les tickets assignés à son pôle" },
];

export default function Admin() {
  const { poleLabel, reloadPoles } = usePoles();
  const { user } = useAuth() || {};
  const isAdmin = user?.role === "admin";
  const [poles, setPoles] = useState([]);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testSending, setTestSending] = useState(false);

  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [pwdTarget, setPwdTarget] = useState(null);

  // Pole management state
  const [poleCreateOpen, setPoleCreateOpen] = useState(false);
  const [poleRenameTarget, setPoleRenameTarget] = useState(null);

  const load = async () => {
    const { data } = await apiClient.get("/poles");
    setPoles(data);
    reloadPoles();
    const map = {};
    data.forEach((p) => (map[p.id] = p.email || ""));
    setEdits(map);
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const { data } = await apiClient.get("/auth/users");
      setUsers(data);
    } catch (err) {
      console.error("loadUsers failed", err);
      toast.error(err.response?.data?.detail || "Impossible de charger les utilisateurs");
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (isAdmin) {
      loadNotifs();
      loadUsers();
    }
  }, [isAdmin]);

  const loadNotifs = async () => {
    setNotifLoading(true);
    try {
      const { data } = await apiClient.get("/admin/notifications?limit=30");
      setNotifications(data);
    } catch (err) {
      console.warn("loadNotifs failed", err);
    } finally { setNotifLoading(false); }
  };

  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Saisissez un e-mail"); return; }
    setTestSending(true);
    try {
      const { data } = await apiClient.post("/admin/notifications/test-send", {
        to: testTo.trim(),
        subject: "Test Ecocasse — Envoi manuel",
        body: "Ceci est un e-mail de test envoyé depuis le back-office admin.",
      });
      if (data.success) {
        toast.success(`E-mail envoyé (id: ${data.id?.slice(0, 8)}…)`);
        loadNotifs();
      } else {
        toast.error(data.error || "Échec");
      }
    } catch (e) { toast.error("Échec"); } finally { setTestSending(false); }
  };

  const save = async (id) => {
    setSaving((s) => ({ ...s, [id]: true }));
    try {
      await apiClient.put(`/poles/${id}/email`, { email: edits[id] || null });
      toast.success("Email mis à jour");
      load();
    } catch (e) {
      toast.error("Échec");
    } finally {
      setSaving((s) => ({ ...s, [id]: false }));
    }
  };

  return (
    <>
      <Topbar title="Back office Admin" subtitle="Configuration des pôles et des notifications email" />

      <main className="px-4 sm:px-6 lg:px-8 py-5 sm:py-8 max-w-4xl w-full" data-testid="admin-page">
        <div className="border border-zinc-200 bg-blue-50/50 px-5 py-4 flex items-start gap-3 mb-6">
          <Info size={20} weight="duotone" className="text-blue-700 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <div className="font-semibold mb-1">Notifications email — à venir</div>
            <div className="text-blue-800/80">Configurez ici l'adresse email de chaque pôle. Les notifications automatiques seront activées dans une prochaine mise à jour.</div>
          </div>
        </div>

        <div className="bg-white border border-zinc-200">
          <div className="px-6 py-5 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-heading text-lg font-semibold tracking-tight">Pôles &amp; e-mails</h2>
              <p className="text-sm text-zinc-500 mt-1">Créez, renommez, désactivez ou supprimez les pôles. Les archives sont conservées.</p>
            </div>
            {isAdmin && (
              <Button
                onClick={() => setPoleCreateOpen(true)}
                className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm h-10 gap-2"
                data-testid="btn-create-pole"
              >
                <Plus size={16} weight="bold" /> Nouveau pôle
              </Button>
            )}
          </div>
          <div className="divide-y divide-zinc-100">
            {poles.map((p) => (
              <div key={p.id} className={`px-6 py-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end ${p.is_active === false ? "opacity-60" : ""}`} data-testid={`admin-pole-${p.id}`}>
                <div className="md:col-span-3">
                  <div className="label-tiny text-zinc-400">Pôle</div>
                  <div className="font-medium text-zinc-900 mt-1 flex items-center gap-2">
                    <Buildings size={14} weight="duotone" className="text-zinc-400" />
                    <span>{p.name || POLE_LABELS[p.id]}</span>
                    {p.is_active === false && <span className="text-[10px] px-1.5 py-0.5 bg-zinc-200 text-zinc-600 uppercase">Désactivé</span>}
                  </div>
                  <div className="font-mono text-[11px] text-zinc-400">/{p.id}</div>
                </div>
                <div className="md:col-span-5 space-y-1.5">
                  <Label className="label-tiny text-zinc-500">Email</Label>
                  <div className="relative">
                    <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input
                      type="email"
                      value={edits[p.id] || ""}
                      onChange={(e) => setEdits({ ...edits, [p.id]: e.target.value })}
                      placeholder={`${p.id}@entreprise.fr`}
                      className="h-10 pl-9 rounded-sm border-zinc-300"
                      data-testid={`email-input-${p.id}`}
                    />
                  </div>
                </div>
                <div className="md:col-span-4 flex flex-wrap gap-1.5 justify-end">
                  <Button
                    onClick={() => save(p.id)}
                    disabled={saving[p.id]}
                    className="h-10 bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm gap-2 px-3"
                    data-testid={`save-email-${p.id}`}
                  >
                    <FloppyDisk size={14} weight="bold" /> {saving[p.id] ? "..." : "Enregistrer"}
                  </Button>
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => setPoleRenameTarget(p)}
                        className="h-10 px-2.5 border border-zinc-300 hover:bg-zinc-50 text-zinc-700 inline-flex items-center gap-1.5 text-xs"
                        title="Renommer"
                        data-testid={`pole-rename-${p.id}`}
                      >
                        <PencilSimple size={13} weight="bold" />
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await apiClient.patch(`/poles/${p.id}/active`, { is_active: p.is_active === false });
                            toast.success(p.is_active === false ? "Pôle réactivé" : "Pôle désactivé");
                            load();
                          } catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
                        }}
                        className={`h-10 px-2.5 border inline-flex items-center gap-1.5 text-xs ${p.is_active === false ? "border-green-300 text-green-700 hover:bg-green-50" : "border-amber-300 text-amber-700 hover:bg-amber-50"}`}
                        title={p.is_active === false ? "Réactiver" : "Désactiver"}
                        data-testid={`pole-toggle-${p.id}`}
                      >
                        <Power size={13} weight="bold" />
                      </button>
                      <button
                        onClick={async () => {
                          const label = p.name || POLE_LABELS[p.id];
                          if (!window.confirm(`Supprimer le pôle « ${label} » ?\n\nLes demandes PDF et tickets historiques resteront visibles dans les archives globales. Cette action est irréversible.`)) return;
                          try {
                            const { data } = await apiClient.delete(`/poles/${p.id}`);
                            toast.success(`Pôle supprimé — ${data.archived_requests || 0} demande(s) et ${data.archived_tickets || 0} ticket(s) conservés en archives.`);
                            load();
                            loadUsers();
                          } catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
                        }}
                        className="h-10 px-2.5 border border-red-300 hover:bg-red-50 text-red-600 inline-flex items-center gap-1.5 text-xs"
                        title="Supprimer"
                        data-testid={`pole-delete-${p.id}`}
                      >
                        <Trash size={13} weight="bold" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {isAdmin && (
          <div className="bg-white border border-zinc-200 mt-6" data-testid="admin-users-section">
            <div className="px-6 py-5 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="font-heading text-lg font-semibold tracking-tight">Comptes utilisateurs · back-office</h2>
                <p className="text-sm text-zinc-500 mt-1">Créez, modifiez ou supprimez les comptes de connexion (identifiant e-mail + mot de passe).</p>
              </div>
              <Button
                onClick={() => setCreateOpen(true)}
                className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm h-10 gap-2"
                data-testid="btn-create-user"
              >
                <UserPlus size={16} weight="bold" /> Nouveau compte
              </Button>
            </div>
            {usersLoading ? (
              <div className="px-6 py-10 text-sm text-zinc-500 text-center">Chargement...</div>
            ) : users.length === 0 ? (
              <div className="px-6 py-10 text-sm text-zinc-500 text-center">Aucun compte enregistré</div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 border-b border-zinc-200">
                      <tr>
                        <th className="text-left px-4 py-3 label-tiny text-zinc-500">Nom</th>
                        <th className="text-left px-4 py-3 label-tiny text-zinc-500">E-mail (identifiant)</th>
                        <th className="text-left px-4 py-3 label-tiny text-zinc-500">Rôle</th>
                        <th className="text-left px-4 py-3 label-tiny text-zinc-500">Pôle</th>
                        <th className="text-left px-4 py-3 label-tiny text-zinc-500">Actif</th>
                        <th className="text-left px-4 py-3 label-tiny text-zinc-500">Créé le</th>
                        <th className="text-right px-4 py-3 label-tiny text-zinc-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.filter((u) => u.role !== "client").map((u) => (
                        <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`user-row-${u.id}`}>
                          <td className="px-4 py-3 font-medium">{u.name}</td>
                          <td className="px-4 py-3 text-xs font-mono text-zinc-700">{u.email}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium ${u.role === "admin" ? "bg-purple-100 text-purple-800" : u.role === "responsable" ? "bg-blue-100 text-blue-800" : "bg-zinc-100 text-zinc-700"}`}>
                              {ROLES.find((r) => r.id === u.role)?.label || u.role}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs">{u.service_id ? poleLabel(u.service_id) : <span className="text-zinc-400">—</span>}</td>
                          <td className="px-4 py-3">
                            {u.is_active ? (
                              <span className="inline-flex items-center gap-1 text-[11px] text-green-700"><CheckCircle size={12} weight="fill" /> Actif</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500"><XCircle size={12} weight="fill" /> Désactivé</span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-mono text-[11px] text-zinc-500">{formatDate(u.created_at)}</td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <div className="inline-flex gap-1">
                              <button
                                onClick={() => setEditTarget(u)}
                                className="p-1.5 hover:bg-zinc-100 text-zinc-700"
                                title="Modifier"
                                data-testid={`edit-user-${u.id}`}
                              >
                                <PencilSimple size={14} weight="bold" />
                              </button>
                              <button
                                onClick={() => setPwdTarget(u)}
                                className="p-1.5 hover:bg-zinc-100 text-zinc-700"
                                title="Réinitialiser le mot de passe"
                                data-testid={`pwd-user-${u.id}`}
                              >
                                <Key size={14} weight="bold" />
                              </button>
                              <button
                                onClick={async () => {
                                  if (u.id === user?.id) { toast.error("Vous ne pouvez pas vous supprimer vous-même"); return; }
                                  if (!window.confirm(`Supprimer définitivement ${u.email} ?`)) return;
                                  try {
                                    await apiClient.delete(`/auth/users/${u.id}`);
                                    toast.success("Compte supprimé");
                                    loadUsers();
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || "Échec");
                                  }
                                }}
                                className="p-1.5 hover:bg-red-50 text-red-600"
                                title="Supprimer"
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash size={14} weight="bold" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-zinc-100">
                  {users.filter((u) => u.role !== "client").map((u) => (
                    <div key={u.id} className="px-4 py-3" data-testid={`user-card-${u.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium">{u.name}</div>
                          <div className="text-xs font-mono text-zinc-700 truncate">{u.email}</div>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium ${u.role === "admin" ? "bg-purple-100 text-purple-800" : u.role === "responsable" ? "bg-blue-100 text-blue-800" : "bg-zinc-100 text-zinc-700"}`}>
                              {ROLES.find((r) => r.id === u.role)?.label || u.role}
                            </span>
                            {u.service_id && <span className="text-[10px] px-1.5 py-0.5 bg-zinc-100">{poleLabel(u.service_id)}</span>}
                            {!u.is_active && <span className="text-[10px] text-red-600">Désactivé</span>}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <button onClick={() => setEditTarget(u)} className="p-1.5 hover:bg-zinc-100" data-testid={`m-edit-user-${u.id}`}>
                            <PencilSimple size={14} weight="bold" />
                          </button>
                          <button onClick={() => setPwdTarget(u)} className="p-1.5 hover:bg-zinc-100" data-testid={`m-pwd-user-${u.id}`}>
                            <Key size={14} weight="bold" />
                          </button>
                          <button
                            onClick={async () => {
                              if (u.id === user?.id) { toast.error("Vous ne pouvez pas vous supprimer vous-même"); return; }
                              if (!window.confirm(`Supprimer ${u.email} ?`)) return;
                              try { await apiClient.delete(`/auth/users/${u.id}`); toast.success("Supprimé"); loadUsers(); } catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
                            }}
                            className="p-1.5 hover:bg-red-50 text-red-600"
                            data-testid={`m-delete-user-${u.id}`}
                          >
                            <Trash size={14} weight="bold" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {isAdmin && (
          <div className="bg-white border border-zinc-200 mt-6" data-testid="admin-notifications-section">
            <div className="px-6 py-5 border-b border-zinc-200">
              <h2 className="font-heading text-lg font-semibold tracking-tight">Notifications e-mail (Resend)</h2>
              <p className="text-sm text-zinc-500 mt-1">Historique des envois et test manuel. Expéditeur : <code className="text-[11px] bg-zinc-100 px-1">noreply@suivi.ecocasse.trouvermapiece.fr</code></p>
            </div>
            <div className="px-6 py-4 border-b border-zinc-200 bg-zinc-50">
              <Label className="label-tiny text-zinc-500 mb-1.5 block">Envoyer un e-mail de test</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="destinataire@example.com"
                  className="h-10 rounded-sm border-zinc-300 flex-1"
                  data-testid="test-email-input"
                />
                <Button
                  onClick={sendTest}
                  disabled={testSending}
                  className="h-10 bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm px-4 gap-2"
                  data-testid="test-email-send"
                >
                  <PaperPlaneTilt size={14} weight="bold" />
                  {testSending ? "Envoi..." : "Envoyer"}
                </Button>
              </div>
              <p className="text-[11px] text-zinc-500 mt-2">
                ✅ Domaine <code>suivi.ecocasse.trouvermapiece.fr</code> vérifié — les e-mails sont envoyés à tous les destinataires. Pour changer l&apos;expéditeur, modifiez <code>SENDER_EMAIL</code> dans <code>backend/.env</code>.
              </p>
            </div>
            <div className="divide-y divide-zinc-100">
              {notifLoading ? (
                <div className="px-6 py-8 text-sm text-zinc-500 text-center">Chargement...</div>
              ) : notifications.length === 0 ? (
                <div className="px-6 py-8 text-sm text-zinc-500 text-center">Aucune notification pour le moment</div>
              ) : notifications.map((n) => (
                <div key={n.id} className="px-6 py-3 flex items-center gap-3" data-testid={`notification-${n.id}`}>
                  {n.success ? <CheckCircle size={16} weight="fill" className="text-green-600 shrink-0" /> : <XCircle size={16} weight="fill" className="text-red-500 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate"><span className="font-medium">{n.subject}</span></div>
                    <div className="text-xs text-zinc-500 truncate">{n.recipient_email} · {n.event}</div>
                    {n.error && <div className="text-[11px] text-red-600 truncate">{n.error}</div>}
                    {n.provider_id && (
                      <a
                        href={`https://resend.com/emails/${n.provider_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-block text-[10px] text-zinc-400 hover:text-zinc-700 font-mono truncate mt-0.5"
                        title="Voir l'e-mail dans Resend"
                        data-testid={`notification-provider-${n.id}`}
                      >
                        Resend ID: {n.provider_id}
                      </a>
                    )}
                  </div>
                  <div className="font-mono text-[11px] text-zinc-500 shrink-0">{formatDate(n.sent_at)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Create user dialog */}
      <CreateUserDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={loadUsers}
        poles={poles}
      />

      {/* Edit user dialog */}
      <EditUserDialog
        target={editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
        onSaved={loadUsers}
        poles={poles}
      />

      {/* Password reset dialog */}
      <ResetPasswordDialog
        target={pwdTarget}
        onOpenChange={(o) => !o && setPwdTarget(null)}
      />

      {/* Create pole dialog */}
      <CreatePoleDialog
        open={poleCreateOpen}
        onOpenChange={setPoleCreateOpen}
        onCreated={load}
      />

      {/* Rename pole dialog */}
      <RenamePoleDialog
        target={poleRenameTarget}
        onOpenChange={(o) => !o && setPoleRenameTarget(null)}
        onSaved={load}
      />
    </>
  );
}

function CreateUserDialog({ open, onOpenChange, onCreated, poles }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("employe");
  const [serviceId, setServiceId] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName(""); setEmail(""); setRole("employe"); setServiceId(""); setPassword(""); setShowPwd(false);
    }
  }, [open]);

  const generatePwd = () => {
    const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let out = "";
    for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
    setPassword(out);
    setShowPwd(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("E-mail invalide"); return; }
    if (password.length < 6) { toast.error("Mot de passe : min. 6 caractères"); return; }
    if (role !== "admin" && !serviceId) { toast.error("Choisissez un pôle pour ce rôle"); return; }
    setSubmitting(true);
    try {
      await apiClient.post("/auth/staff", {
        email: email.trim().toLowerCase(),
        password,
        name: name.trim() || email.split("@")[0],
        role,
        service_id: role === "admin" ? null : serviceId,
      });
      toast.success(`Compte ${email} créé avec succès`);
      onCreated && onCreated();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><UserPlus size={18} weight="bold" /> Nouveau compte back-office</DialogTitle>
          <DialogDescription>
            Créez un identifiant e-mail + mot de passe. L&apos;utilisateur pourra se connecter via /login.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="label-tiny text-zinc-500">Nom complet</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Marie Martin" className="h-10 rounded-sm border-zinc-300" data-testid="create-user-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="label-tiny text-zinc-500">E-mail (identifiant de connexion) *</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="prenom.nom@ecocasse.fr" className="h-10 rounded-sm border-zinc-300 font-mono" required autoFocus data-testid="create-user-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-tiny text-zinc-500">Rôle *</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="h-10 rounded-sm border-zinc-300" data-testid="create-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {role !== "admin" && (
              <div className="space-y-1.5">
                <Label className="label-tiny text-zinc-500">Pôle *</Label>
                <Select value={serviceId} onValueChange={setServiceId}>
                  <SelectTrigger className="h-10 rounded-sm border-zinc-300" data-testid="create-user-pole"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>
                    {poles.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name || POLE_LABELS[p.id]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="label-tiny text-zinc-500">Mot de passe *</Label>
            <div className="flex gap-2">
              <Input type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 6 caractères" className="h-10 rounded-sm border-zinc-300 font-mono" required data-testid="create-user-password" />
              <Button type="button" variant="outline" onClick={generatePwd} className="rounded-sm h-10 px-3 text-xs" data-testid="create-user-generate">Générer</Button>
            </div>
            {password && (
              <div className="flex items-center justify-between gap-2 text-[11px] text-zinc-500">
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={showPwd} onChange={(e) => setShowPwd(e.target.checked)} /> Afficher</label>
                <button type="button" onClick={() => { navigator.clipboard.writeText(password); toast.success("Copié"); }} className="inline-flex items-center gap-1 hover:text-zinc-900">
                  <Copy size={11} weight="bold" /> Copier
                </button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button>
            <Button type="submit" disabled={submitting} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm" data-testid="create-user-submit">
              {submitting ? "Création..." : "Créer le compte"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ target, onOpenChange, onSaved, poles }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("employe");
  const [serviceId, setServiceId] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (target) {
      setName(target.name || "");
      setRole(target.role);
      setServiceId(target.service_id || "");
      setIsActive(target.is_active !== false);
    }
  }, [target]);

  if (!target) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (role !== "admin" && !serviceId) { toast.error("Choisissez un pôle"); return; }
    setSubmitting(true);
    try {
      await apiClient.patch(`/auth/users/${target.id}`, {
        name: name.trim(),
        role,
        service_id: role === "admin" ? "" : serviceId,
        is_active: isActive,
      });
      toast.success("Compte mis à jour");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><PencilSimple size={18} weight="bold" /> Modifier {target.email}</DialogTitle>
          <DialogDescription>Modifiez le nom, le rôle, le pôle ou désactivez le compte.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="label-tiny text-zinc-500">Nom complet</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-10 rounded-sm border-zinc-300" data-testid="edit-user-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-tiny text-zinc-500">Rôle</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="h-10 rounded-sm border-zinc-300" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {role !== "admin" && (
              <div className="space-y-1.5">
                <Label className="label-tiny text-zinc-500">Pôle</Label>
                <Select value={serviceId} onValueChange={setServiceId}>
                  <SelectTrigger className="h-10 rounded-sm border-zinc-300" data-testid="edit-user-pole"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>
                    {poles.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name || POLE_LABELS[p.id]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} data-testid="edit-user-active" />
            <span>Compte actif (autorisé à se connecter)</span>
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button>
            <Button type="submit" disabled={submitting} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm" data-testid="edit-user-submit">
              {submitting ? "..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ target, onOpenChange }) {
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (target) { setPassword(""); setShowPwd(true); }
  }, [target]);

  if (!target) return null;

  const generatePwd = () => {
    const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let out = "";
    for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
    setPassword(out);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Min. 6 caractères"); return; }
    setSubmitting(true);
    try {
      await apiClient.patch(`/auth/users/${target.id}`, { password });
      toast.success(`Mot de passe réinitialisé pour ${target.email}`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="pwd-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Key size={18} weight="bold" /> Réinitialiser le mot de passe</DialogTitle>
          <DialogDescription>
            Nouveau mot de passe pour <span className="font-mono text-zinc-900">{target.email}</span>. Transmettez-le à l&apos;utilisateur par un canal sûr.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="label-tiny text-zinc-500">Nouveau mot de passe *</Label>
            <div className="flex gap-2">
              <Input type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 6 caractères" className="h-10 rounded-sm border-zinc-300 font-mono" required autoFocus data-testid="pwd-input" />
              <Button type="button" variant="outline" onClick={generatePwd} className="rounded-sm h-10 px-3 text-xs" data-testid="pwd-generate">Générer</Button>
            </div>
            {password && (
              <div className="flex items-center justify-between gap-2 text-[11px] text-zinc-500">
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={showPwd} onChange={(e) => setShowPwd(e.target.checked)} /> Afficher</label>
                <button type="button" onClick={() => { navigator.clipboard.writeText(password); toast.success("Copié"); }} className="inline-flex items-center gap-1 hover:text-zinc-900">
                  <Copy size={11} weight="bold" /> Copier
                </button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button>
            <Button type="submit" disabled={submitting} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm" data-testid="pwd-submit">
              {submitting ? "..." : "Réinitialiser"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreatePoleDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [poleId, setPoleId] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [idTouched, setIdTouched] = useState(false);

  useEffect(() => {
    if (open) { setName(""); setPoleId(""); setEmail(""); setIdTouched(false); }
  }, [open]);

  const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 32);

  const handleNameChange = (e) => {
    const v = e.target.value;
    setName(v);
    if (!idTouched) setPoleId(slugify(v));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Nom requis"); return; }
    if (!poleId || !/^[a-z0-9_]{2,32}$/.test(poleId)) { toast.error("Identifiant : 2-32 caractères a-z, 0-9 ou _"); return; }
    setSubmitting(true);
    try {
      await apiClient.post("/poles", { id: poleId, name: name.trim(), email: email.trim() || null });
      toast.success(`Pôle « ${name} » créé`);
      onCreated && onCreated();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="create-pole-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Buildings size={18} weight="bold" /> Nouveau pôle</DialogTitle>
          <DialogDescription>Créez un pôle destinataire (ex: Atelier, Logistique...). Les utilisateurs pourront lui être rattachés.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="label-tiny text-zinc-500">Nom affiché *</Label>
            <Input value={name} onChange={handleNameChange} placeholder="Ex: Atelier" className="h-10 rounded-sm border-zinc-300" required autoFocus data-testid="create-pole-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="label-tiny text-zinc-500">Identifiant (URL /pole/…) *</Label>
            <Input
              value={poleId}
              onChange={(e) => { setIdTouched(true); setPoleId(slugify(e.target.value)); }}
              placeholder="atelier"
              className="h-10 rounded-sm border-zinc-300 font-mono"
              required
              data-testid="create-pole-id"
            />
            <p className="text-[11px] text-zinc-500">Minuscules, chiffres, underscore uniquement. Ne peut plus être modifié après.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="label-tiny text-zinc-500">E-mail (facultatif)</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="atelier@entreprise.fr" className="h-10 rounded-sm border-zinc-300" data-testid="create-pole-email" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button>
            <Button type="submit" disabled={submitting} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm" data-testid="create-pole-submit">
              {submitting ? "..." : "Créer le pôle"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RenamePoleDialog({ target, onOpenChange, onSaved }) {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (target) setName(target.name || POLE_LABELS[target.id] || ""); }, [target]);

  if (!target) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Nom requis"); return; }
    setSubmitting(true);
    try {
      await apiClient.patch(`/poles/${target.id}`, { name: name.trim() });
      toast.success("Pôle renommé");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="rename-pole-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><PencilSimple size={18} weight="bold" /> Renommer le pôle</DialogTitle>
          <DialogDescription>Identifiant : <code className="font-mono text-zinc-900">/{target.id}</code> (non modifiable)</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="label-tiny text-zinc-500">Nouveau nom *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-10 rounded-sm border-zinc-300" required autoFocus data-testid="rename-pole-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button>
            <Button type="submit" disabled={submitting} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm" data-testid="rename-pole-submit">
              {submitting ? "..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

